import { XIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import AttentionIcon from '@/components/icons/attention.svg?react'
import FailIcon from '@/components/icons/fail.svg?react'
import InfoIcon from '@/components/icons/info.svg?react'
import SuccessIcon from '@/components/icons/success.svg?react'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'

import { StandardToast, ToastButton } from './types'

interface ToastProps {
  toast: StandardToast
  onClose: () => void
}
export function Toast({ toast, onClose }: ToastProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const startTimeRef = useRef<number | null>(null)
  const remainingTimeRef = useRef<number | null>(null)

  const { type, title, description, icon: customIcon, options: { duration = 2500, showCloseButton, buttons = [] } = {} } = toast

  const shouldAutoClose = buttons.length === 0 && !toast.options?.disableAutoClose

  useEffect(() => {
    if (shouldAutoClose && duration > 0 && !isFocused) {
      const timeToWait = remainingTimeRef.current ?? duration

      // If timer expired while focused, close after a brief delay to avoid immediate close
      if (timeToWait <= 0) {
        timerRef.current = setTimeout(() => {
          onClose()
        }, 100)
        return () => {
          if (timerRef.current) {
            clearTimeout(timerRef.current)
            timerRef.current = null
          }
        }
      }

      startTimeRef.current = Date.now()

      timerRef.current = setTimeout(() => {
        onClose()
      }, timeToWait)

      return () => {
        if (timerRef.current && startTimeRef.current) {
          clearTimeout(timerRef.current)
          timerRef.current = null
          const elapsed = Date.now() - startTimeRef.current
          remainingTimeRef.current = Math.max(0, timeToWait - elapsed)
        }
      }
    } else {
      // When focused or auto-close is disabled, ensure timer is cleared
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [shouldAutoClose, duration, onClose, isFocused])

  const handleFocus = () => {
    setIsFocused(true)
    if (timerRef.current && startTimeRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
      const elapsed = Date.now() - startTimeRef.current
      // Calculate remaining time based on what was remaining when this timer started
      const timeToWait = remainingTimeRef.current ?? duration
      remainingTimeRef.current = Math.max(0, timeToWait - elapsed)
      startTimeRef.current = null
    } else if (!startTimeRef.current && remainingTimeRef.current === null) {
      // If timer hasn't started yet, initialize remaining time
      remainingTimeRef.current = duration
    }
  }

  const handleBlur = () => {
    // setIsFocused(false)
    // Reset startTimeRef so useEffect can properly restart the timer
    startTimeRef.current = null
  }

  const getDefaultIcon = () => {
    switch (type) {
      case 'success':
        return <SuccessIcon className='size-6' />
      case 'error':
        return <FailIcon className='size-6' />
      case 'warning':
        return <AttentionIcon className='size-6' />
      case 'info':
        return <InfoIcon className='size-6' />
      default:
        return null
    }
  }

  const icon = customIcon || getDefaultIcon()

  const handleButtonClick = async (button: ToastButton) => {
    setIsLoading(true)
    try {
      await button.onClick()
    } catch (error) {
      console.error('Error executing toast button action:', error)
    } finally {
      setIsLoading(false)
      onClose()
    }
  }

  const displayCloseButton = showCloseButton !== undefined ? showCloseButton : buttons.length === 0

  return (
    <div
      className={cn(
        'relative flex min-h-[200px] w-full max-w-[328px] min-w-[400px] flex-col items-center justify-center gap-[24px] overflow-clip rounded-2xl bg-white p-6 shadow-[0px_2px_6px_0px_rgba(0,0,0,0.1)]',
        toast.options?.className
      )}
      dir='rtl'
      tabIndex={0}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      {displayCloseButton && (
        <button
          className='absolute start-2 top-2 flex size-6 cursor-pointer items-center justify-center rounded-full border-none bg-transparent text-xl text-[#666] transition-colors hover:bg-black/5 hover:text-[#1c1c1c]'
          onClick={onClose}
          aria-label='Close toast'
        >
          <XIcon className='size-4' />
          <span className='sr-only'>Close</span>
        </button>
      )}

      {icon && <div className='flex shrink-0'>{icon}</div>}

      <div className='flex w-full flex-col items-center gap-2 text-center'>
        <div className='flex shrink-0 flex-col justify-center text-base leading-6 font-medium text-[#333333]'>
          <p className='leading-6 whitespace-pre' dir='auto'>
            {title}
          </p>
        </div>
        {description && (
          <div className='flex w-[min-content] min-w-full shrink-0 flex-col justify-center text-sm font-normal text-[#616161]'>
            <p className='leading-5' dir='auto'>
              {description}
            </p>
          </div>
        )}
      </div>

      {buttons.length > 0 && (
        <div className='flex w-4/5 items-center justify-between'>
          {isLoading ? (
            <div className='flex h-5 w-full items-center justify-center'>
              <Spinner className='size-5' />
            </div>
          ) : (
            buttons.map((button, idx) => (
              <button
                key={idx}
                className={cn(
                  'cursor-pointer text-right text-sm leading-5 font-normal text-[#666] transition-colors',
                  button.primary && 'font-medium text-[#1c1c1c]'
                )}
                onClick={() => handleButtonClick(button)}
              >
                {button.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}
